import React, { useState, useEffect } from 'react';
import { Route, Switch } from 'react-router';
import { useHistory } from 'react-router-dom';
import { ContentContainer } from '../../components/ContentContainer';
import { GenericToolbar } from '../../components/GenericToolbar/GenericToolbar';
import { pushAppRoute } from '../../data/domain/route-manager';
import { AppRoute, AppRoutes } from '../../routes';
import { ChildDeviceProfileData } from '../../components/DeviceProfile/ChildProfileData';
import { useSierraChildDeviceDetails } from '../../data/DataProviders/SierraChildDeviceDetailsProvider';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { EditChildDevice } from '../../components/DeviceProfile/EditChildDevice';
import { DataIndicators } from '../../components/DataIndicators';
import { SierraChildDeviceData } from '../../types/device';
import { isDataLoaded } from '../../types';
import { DeviceDetailsProvider } from '../../data/DataProviders/DeviceDetailsProvider';
import {
    SierraGatewayDeviceDetailsProvider,
    useSierraGatewayDeviceDetails,
} from '../../data/DataProviders/SierraGatewayDeviceDetailsProvider';
import { DeviceMasterProvider } from '../../data/DataProviders/DeviceMasterProvider';
import { SignalRSiteDataProvider } from '../../data/DataProviders/SignalRSiteDataProvider';

export const ChildDeviceProfile: React.FC = () => {
    const selectedOrganization = useSelectedOrganizationData();
    const history = useHistory();
    const currentAppRoute = useCurrentAppRoute();
    const childSierraDeviceDetails = useSierraChildDeviceDetails();
    const sierraDeviceDetails = useSierraGatewayDeviceDetails();

    const [childDeviceData, setChildDeviceData] = useState<SierraChildDeviceData | null>(null);
    const [headerDeviceName, setHeaderDeviceName] = useState('');
    const currentPage = currentAppRoute.type === 'ChildDeviceProfile' ? 'ChildDeviceProfile' : 'EditChildDevice';

    const updatedChildData = (modifiedData: SierraChildDeviceData): void => {
        if (isDataLoaded(childSierraDeviceDetails)) {
            const data = { ...childSierraDeviceDetails.value };
            data.ipAddress = modifiedData.ipAddress;
            data.unitId = modifiedData.unitId;
            data.name = modifiedData.name;
            data.ipPort = modifiedData.ipPort;
            setChildDeviceData(data);
            setHeaderDeviceName(modifiedData.name);
        }
    };
    useEffect(() => {
        if (isDataLoaded(childSierraDeviceDetails)) {
            setChildDeviceData(childSierraDeviceDetails.value);
            setHeaderDeviceName(childSierraDeviceDetails.value.name);
        }
    }, [childSierraDeviceDetails, sierraDeviceDetails]);

    const backRoute = (
        orgId: string,
        locationId: string,
        siteId: string,
        deviceId: string,
        childDeviceId: string
    ): AppRoute =>
        currentPage === 'ChildDeviceProfile'
            ? { type: 'DeviceProfile', orgId: orgId, locationId: locationId, siteId: siteId, deviceId: deviceId }
            : {
                  type: 'ChildDeviceProfile',
                  deviceId: deviceId,
                  locationId: locationId,
                  orgId: orgId,
                  siteId: siteId,
                  childDeviceId: childDeviceId,
              };

    return (
        <>
            {isRecordFound(selectedOrganization) &&
                'locationId' in currentAppRoute &&
                'deviceId' in currentAppRoute &&
                'siteId' in currentAppRoute &&
                'childDeviceId' in currentAppRoute && (
                    <>
                        <GenericToolbar
                            showBackButton
                            backButtonOnClick={(): void => {
                                pushAppRoute(
                                    history,
                                    backRoute(
                                        selectedOrganization.value.id,
                                        currentAppRoute.locationId,
                                        currentAppRoute.siteId,
                                        currentAppRoute.deviceId,
                                        currentAppRoute.childDeviceId
                                    )
                                );
                            }}
                            title={currentPage === 'ChildDeviceProfile' ? headerDeviceName : 'Edit Device'}
                            subtitle={currentPage === 'ChildDeviceProfile' ? 'Device Profile' : headerDeviceName}
                        />

                        <DataIndicators
                            emptyIcon="Devices"
                            emptyMessage={'No Device Data'}
                            errorMessage={'No Device Data'}
                            data={childSierraDeviceDetails}
                            isEmpty={false}
                        />
                        <ContentContainer>
                            <Switch>
                                <SierraGatewayDeviceDetailsProvider
                                    deviceId={currentAppRoute.deviceId}
                                    siteId={currentAppRoute.siteId}
                                >
                                    <Route exact path={AppRoutes.ChildDeviceProfile}>
                                        <DeviceDetailsProvider
                                            deviceId={currentAppRoute.childDeviceId}
                                            recursive={false}
                                        >
                                        <SignalRSiteDataProvider siteId={currentAppRoute.siteId} locationId={currentAppRoute.locationId}>
                                            <ChildDeviceProfileData
                                                sierraChildDeviceDetails={childDeviceData}
                                                backAppRoute={backRoute(
                                                    selectedOrganization.value.id,
                                                    currentAppRoute.locationId,
                                                    currentAppRoute.siteId,
                                                    currentAppRoute.deviceId,
                                                    currentAppRoute.childDeviceId
                                                )}
                                            />
                                            </SignalRSiteDataProvider>
                                        </DeviceDetailsProvider>
                                    </Route>
                                    <Route exact path={AppRoutes.EditDeviceProfile}>
                                        <DeviceMasterProvider>
                                            <EditChildDevice
                                                sierraChildDeviceDetails={childDeviceData}
                                                updatedChildData={updatedChildData}
                                            />
                                        </DeviceMasterProvider>
                                    </Route>
                                </SierraGatewayDeviceDetailsProvider>
                            </Switch>
                        </ContentContainer>
                    </>
                )}
        </>
    )
};
